import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { neighborhoods, neighborhoodCategories } from '../../../mocks/neighborhoods';
import { neighborhoodDetails } from '../../../mocks/neighborhoodDetails';
import type { Neighborhood } from '../../../mocks/neighborhoods';

function NeighborhoodCard({
  neighborhood,
  isActive,
  onClick,
}: {
  neighborhood: Neighborhood;
  isActive: boolean;
  onClick: () => void;
}) {
  const [showBusinesses, setShowBusinesses] = useState(false);
  const detail = neighborhoodDetails.find((n) => n.id === neighborhood.id);
  const slug = detail?.slug || '';

  return (
    <div
      onClick={onClick}
      className={`rounded-2xl border transition-all duration-300 cursor-pointer overflow-hidden ${
        isActive
          ? 'border-teal-300 shadow-lg ring-1 ring-teal-200'
          : 'border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200'
      }`}
      style={isActive ? { borderLeftWidth: '4px', borderLeftColor: neighborhood.accentColor } : {}}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        <div className="sm:w-44 h-36 sm:h-auto flex-shrink-0 overflow-hidden">
          <img
            src={neighborhood.image}
            alt={`${neighborhood.name} Roseville California`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Content */}
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h4 className="text-lg font-bold text-gray-900 leading-tight">
                <Link to={`/neighborhoods/${slug}`} className="hover:text-teal-600 transition-colors" onClick={(e) => e.stopPropagation()}>
                  {neighborhood.name}
                </Link>
              </h4>
              <span
                className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold mt-1 ${neighborhood.accentBg} ${neighborhood.accentText}`}
              >
                {neighborhood.tagline}
              </span>
            </div>
            <div className="text-right flex-shrink-0 ml-3">
              <span className="text-2xl font-black" style={{ color: neighborhood.accentColor }}>
                {neighborhood.businessCount}
              </span>
              <p className="text-[11px] text-gray-500 -mt-0.5">businesses</p>
            </div>
          </div>

          <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 mb-3">
            {neighborhood.description}
          </p>

          {/* Scores Row */}
          <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
            <span className="flex items-center gap-1.5">
              <i className="ri-walk-line w-4 h-4 flex items-center justify-center text-teal-500"></i>
              Walk {neighborhood.walkScore}
            </span>
            <span className="w-px h-3 bg-gray-200"></span>
            <span className="flex items-center gap-1.5">
              <i className="ri-bus-line w-4 h-4 flex items-center justify-center text-teal-500"></i>
              Transit {neighborhood.transitScore}
            </span>
            <span className="w-px h-3 bg-gray-200"></span>
            <span className="flex items-center gap-1.5">
              <i className="ri-restaurant-line w-4 h-4 flex items-center justify-center text-teal-500"></i>
              {neighborhood.diningCount} Restaurants
            </span>
          </div>

          {/* Highlights */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {neighborhood.highlights.map((h) => (
              <span key={h} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[11px] rounded-md font-medium">
                {h}
              </span>
            ))}
          </div>

          {/* Actions Row */}
          <div className="flex items-center gap-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowBusinesses(!showBusinesses);
              }}
              className="text-xs font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className={`ri-arrow-${showBusinesses ? 'up' : 'down'}-s-line`}></i>
              {showBusinesses ? 'Hide' : 'Top'} Businesses
            </button>
            <Link
              to={`/neighborhoods/${slug}`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-semibold text-gray-500 hover:text-teal-600 flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
            >
              View All
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {showBusinesses && (
            <div className="mt-3 space-y-2 animate-fadeIn">
              {neighborhood.topBusinesses.map((biz) => (
                <div key={biz.name} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                      <i className={`${biz.icon} text-sm text-gray-600`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{biz.name}</p>
                      <p className="text-[11px] text-gray-500">{biz.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <i className="ri-star-fill text-amber-400"></i>
                    <span className="font-semibold text-gray-800">{biz.rating}</span>
                    <span className="text-gray-400">({biz.reviews.toLocaleString()})</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function NeighborhoodGuide() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeNeighborhood, setActiveNeighborhood] = useState<number>(1);
  const mapRef = useRef<HTMLIFrameElement>(null);

  const filteredNeighborhoods =
    activeFilter === 'all'
      ? neighborhoods
      : neighborhoods.filter((n) => n.primaryCategory === activeFilter);

  const selectedNeighborhood = neighborhoods.find((n) => n.id === activeNeighborhood) || neighborhoods[0];

  const mapSrc = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12461.5!2d-121.288!3d38.752!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809b2a1c4e5b0c5d%3A0x0!2s${selectedNeighborhood.mapQuery}!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus`;

  useEffect(() => {
    if (filteredNeighborhoods.length > 0 && !filteredNeighborhoods.find((n) => n.id === activeNeighborhood)) {
      setActiveNeighborhood(filteredNeighborhoods[0].id);
    }
  }, [activeFilter, filteredNeighborhoods, activeNeighborhood]);

  return (
    <section id="neighborhoods" className="py-24 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-14">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <span className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 rounded-full text-xs font-semibold tracking-wide uppercase mb-4">
                Explore Districts
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-3">
                Neighborhood<br />Business Guide
              </h2>
              <p className="text-gray-600 max-w-lg text-base">
                Discover Roseville&apos;s vibrant business districts — from historic Old Town charm to premier shopping destinations
              </p>
            </div>

            {/* Aggregate Stats */}
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-3xl font-black text-gray-900">610+</p>
                <p className="text-xs text-gray-500 mt-0.5">Total Businesses</p>
              </div>
              <div className="w-px bg-gray-200"></div>
              <div className="text-center">
                <p className="text-3xl font-black text-gray-900">6</p>
                <p className="text-xs text-gray-500 mt-0.5">Key Districts</p>
              </div>
              <div className="w-px bg-gray-200"></div>
              <div className="text-center">
                <p className="text-3xl font-black text-gray-900">168</p>
                <p className="text-xs text-gray-500 mt-0.5">Restaurants</p>
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          {neighborhoodCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveFilter(cat.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeFilter === cat.id
                  ? 'bg-teal-600 text-white shadow-lg shadow-teal-200'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Main Content: Map + Cards */}
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Map Panel */}
          <div className="lg:col-span-3">
            <div className="sticky top-24">
              {/* Map */}
              <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-100 relative">
                <div className="w-full h-[520px]">
                  <iframe
                    ref={mapRef}
                    src={mapSrc}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`${selectedNeighborhood.name} Map - Roseville CA`}
                    className="absolute inset-0"
                  ></iframe>
                </div>

                {/* Active District Overlay */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${selectedNeighborhood.accentColor}15` }}
                        >
                          <i className="ri-map-pin-2-fill text-xl" style={{ color: selectedNeighborhood.accentColor }}></i>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-sm">{selectedNeighborhood.name}</p>
                          <p className="text-xs text-gray-500">{selectedNeighborhood.tagline} &middot; {selectedNeighborhood.businessCount} businesses</p>
                        </div>
                      </div>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${selectedNeighborhood.mapQuery}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-navigation-line"></i>
                        Open in Maps
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* District Quick Nav */}
              <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
                {neighborhoods.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => setActiveNeighborhood(n.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                      activeNeighborhood === n.id
                        ? 'bg-white shadow-md border border-gray-200 text-gray-900'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: n.accentColor }}
                    ></span>
                    {n.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Cards Panel */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-900">
                {filteredNeighborhoods.length} District{filteredNeighborhoods.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-gray-500">Click to explore on map</p>
            </div>

            {filteredNeighborhoods.map((n) => (
              <NeighborhoodCard
                key={n.id}
                neighborhood={n}
                isActive={activeNeighborhood === n.id}
                onClick={() => setActiveNeighborhood(n.id)}
              />
            ))}

            {filteredNeighborhoods.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <i className="ri-map-pin-line text-4xl text-gray-300 mb-3"></i>
                <p className="text-gray-500 text-sm">No districts match this filter</p>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 text-white mt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-store-3-line text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">Own a Local Business?</h4>
                  <p className="text-white/80 text-sm mb-4 leading-relaxed">
                    Get featured in our neighborhood guide and reach thousands of Roseville residents and visitors.
                  </p>
                  <a
                    href="/advertise"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-teal-700 rounded-lg text-sm font-semibold hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-megaphone-line"></i>
                    Advertise With Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
